import {
  EnvironmentHttpApi,
  EnvironmentHttpCommonError,
  type EnvironmentInternalError,
  type EnvironmentRequestInvalidError,
  type EnvironmentUnauthorizedError,
} from "@afar-research/contracts";
import { httpHeaderRedactionLayer } from "@afar-research/shared/httpObservability";
import * as Data from "effect/Data";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { FetchHttpClient, HttpClient, HttpClientError } from "effect/unstable/http";
import * as HttpApiClient from "effect/unstable/httpapi/HttpApiClient";
import { joinEnvironmentPath } from "../environment/endpoint.ts";

const isEnvironmentHttpCommonError = Schema.is(EnvironmentHttpCommonError);

export class HostEnvironmentFetchError extends Data.TaggedError("HostEnvironmentFetchError")<{
  readonly message: string;
  readonly cause: unknown;
}> {}

export class HostEnvironmentInvalidJsonError extends Data.TaggedError(
  "HostEnvironmentInvalidJsonError",
)<{
  readonly message: string;
  readonly cause: unknown;
}> {}

export class HostEnvironmentUndeclaredStatusError extends Data.TaggedError(
  "HostEnvironmentUndeclaredStatusError",
)<{
  readonly message: string;
  readonly status: number;
  readonly requestUrl: string;
}> {
  constructor(requestUrl: string, status: number) {
    super({
      message: `AFAR Environment endpoint ${requestUrl} returned undeclared status ${status}.`,
      requestUrl,
      status,
    });
  }
}

export class HostEnvironmentTimeoutError extends Data.TaggedError("HostEnvironmentTimeoutError")<{
  readonly message: string;
  readonly requestUrl: string;
  readonly timeoutMs: number;
}> {
  constructor(requestUrl: string, timeoutMs: number) {
    super({
      message: `AFAR Environment endpoint ${requestUrl} timed out after ${timeoutMs}ms.`,
      requestUrl,
      timeoutMs,
    });
  }
}

export type HostEnvironmentRequestError =
  | EnvironmentRequestInvalidError
  | EnvironmentInternalError
  | EnvironmentUnauthorizedError
  | HostEnvironmentFetchError
  | HostEnvironmentInvalidJsonError
  | HostEnvironmentUndeclaredStatusError
  | HostEnvironmentTimeoutError;

export const environmentHttpClientLayer = (
  fetchFn: typeof globalThis.fetch,
): Layer.Layer<HttpClient.HttpClient> =>
  Layer.merge(
    FetchHttpClient.layer.pipe(Layer.provide(Layer.succeed(FetchHttpClient.Fetch, fetchFn))),
    httpHeaderRedactionLayer,
  );

const environmentApiBaseUrl = (httpBaseUrl: string): string => {
  const url = joinEnvironmentPath(httpBaseUrl, "/");
  url.search = "";
  return url.toString();
};

export const makeEnvironmentHttpApiClient = (httpBaseUrl: string) =>
  HttpApiClient.make(EnvironmentHttpApi, {
    baseUrl: environmentApiBaseUrl(httpBaseUrl),
  });

const failEnvironmentRequest = (
  requestUrl: string,
  cause: unknown,
): Effect.Effect<never, HostEnvironmentRequestError> => {
  if (cause instanceof HostEnvironmentTimeoutError) {
    return Effect.fail(cause);
  }
  if (isEnvironmentHttpCommonError(cause)) {
    return Effect.fail(cause);
  }
  if (Schema.isSchemaError(cause)) {
    return Effect.fail(
      new HostEnvironmentInvalidJsonError({
        message: `AFAR Environment endpoint returned an invalid response from ${requestUrl}.`,
        cause,
      }),
    );
  }
  if (HttpClientError.isHttpClientError(cause) && cause.response !== undefined) {
    const response = cause.response;
    if (response.status < 200 || response.status >= 300) {
      return Effect.fail(new HostEnvironmentUndeclaredStatusError(requestUrl, response.status));
    }
    return Effect.fail(
      new HostEnvironmentInvalidJsonError({
        message: `AFAR Environment endpoint returned an invalid response from ${requestUrl}.`,
        cause,
      }),
    );
  }
  return Effect.fail(
    new HostEnvironmentFetchError({
      message: `Failed to fetch host environment endpoint ${requestUrl} (${String(cause)}).`,
      cause,
    }),
  );
};

export const executeEnvironmentHttpRequest = <A, E, R>(
  requestUrl: string,
  timeoutMs: number,
  request: Effect.Effect<A, E, R>,
): Effect.Effect<A, HostEnvironmentRequestError, R> =>
  request.pipe(
    Effect.timeoutOption(Duration.millis(timeoutMs)),
    Effect.flatMap(
      Option.match({
        onNone: () => Effect.fail(new HostEnvironmentTimeoutError(requestUrl, timeoutMs)),
        onSome: Effect.succeed,
      }),
    ),
    Effect.catch((cause) => failEnvironmentRequest(requestUrl, cause)),
  );
